import React from "react";
import {
  Button,
  FormControl,
  Alert,Box,
  Paper,
  Typography,
  alpha,
  SvgIcon,
} from "@mui/material";


import { styled } from "@mui/material/styles";

const GlassTile = styled(Paper)(({ theme }) => ({
  // Standardize the Paper's background gradient here
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.primary.main, // Use primary.main for the gradient start
    0.1 // Set the desired alpha
  )} 0%, ${alpha(
    theme.palette.primary.main, // Use primary.main for the gradient end
    0.05 // Set the desired alpha
  )} 100%)`, // <-- THIS IS THE KEY CHANGE
  // Note: The background-color: #f3f4f5; is likely coming from a base MuiPaper style,
  // but the gradient will overlay it or be the primary background.

  backdropFilter: "blur(20px)",
  border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
  borderRadius: 16,
  padding: "20px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 8,
  minWidth: 135,
  transition: "all 0.3s ease",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: `linear-gradient(90deg, transparent, ${alpha(
      theme.palette.common.white,
      0.3
    )}, transparent)`,
  },
  "&:hover": {
    transform: "translateY(-4px)",
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.primary.main, // Use primary.main for hover gradient start
      0.15 // Slightly higher alpha for hover effect
    )} 0%, ${alpha(
      theme.palette.primary.main, // Use primary.main for hover gradient end
      0.1 // Slightly higher alpha for hover effect
    )} 100%)`, // <-- Also standardize hover background
    border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.2)}`,
  },
  height: "100%", // Make sure tile takes full height of grid item
  minHeight: 180, // Set a minimum height
  width: "100%", // Take full width of grid item
  boxSizing: "border-box", // Include padding in width/height calculations

  // Adjust padding to be more responsive
  padding: theme.spacing(2),

  // Make sure content is properly spaced
  // Update display and alignment
  display: "grid",
  gridTemplateRows: "auto auto auto", // Three rows for icon, label, and value
  alignItems: "center",
  justifyItems: "center",
  gap: theme.spacing(1),
}));

const IconWrapper = styled(Box)(({ theme, iconColor }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 48,
  height: 48,
  borderRadius: "50%",
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.primary.main,
    0.3
  )}, ${alpha(theme.palette.secondary.main, 0.2)})`,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
  "& .MuiSvgIcon-root": {
    color: iconColor || theme.palette.primary.main,
    fontSize: 24,
  },
}));

const ValueText = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "1.25rem",
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  lineHeight: 1.2,
  textAlign: "center",
  margin: 0,
  padding: 0,
}));

const LabelText = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  fontSize: "0.75rem",
  color: alpha(theme.palette.text.primary, 0.8),
  textAlign: "center",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  margin: 0,
  padding: 0,
}));

const GlassTileLayout = ({
  icon: IconComponent,
  label,
  value,
  children,
  iconColor,
}) => {
  return (
    <GlassTile elevation={0}>

      <LabelText>{label}</LabelText>
      <ValueText>{value}</ValueText>

    </GlassTile>
  );
};

// Main container with glassmorphism effect
export const GlassFeedContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.background.paper,
    0.8
  )} 0%, ${alpha(theme.palette.background.paper, 0.6)} 100%)`,
  backdropFilter: "blur(20px)",
  border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
  borderRadius: 16,
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: `linear-gradient(90deg, transparent, ${alpha(
      theme.palette.common.white,
      0.3
    )}, transparent)`,
    zIndex: 1,
  },
}));

// Glassmorphic header section
export const GlassHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5, 3),
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.primary.main,
    0.08
  )} 0%, ${alpha(theme.palette.primary.main, 0.04)} 100%)`,
  backdropFilter: "blur(10px)",
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
  position: "relative",
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: `linear-gradient(90deg, transparent, ${alpha(
      theme.palette.primary.main,
      0.2
    )}, transparent)`,
  },
}));

// Glassmorphic button with hover effects
export const GlassButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.primary.main,
    0.15
  )} 0%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`,
  backdropFilter: "blur(10px)",
  border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
  borderRadius: 8,
  color: theme.palette.primary.main,
  fontWeight: 600,
  textTransform: "none",
  minWidth: "auto",
  padding: theme.spacing(0.75, 1.5),
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.common.white,
      0.1
    )}, transparent)`,
    opacity: 0,
    transition: "opacity 0.3s ease",
  },
  "&:hover": {
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.primary.main,
      0.2
    )} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
    transform: "translateY(-1px)",
    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.25)}`,
    "&::before": {
      opacity: 1,
    },
  },
  "&:disabled": {
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.action.disabled,
      0.1
    )} 0%, ${alpha(theme.palette.action.disabled, 0.05)} 100%)`,
    border: `1px solid ${alpha(theme.palette.action.disabled, 0.15)}`,
    color: theme.palette.action.disabled,
  },
}));

// Glassmorphic select/form control
export const GlassSelect = styled(FormControl)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.background.paper,
      0.6
    )} 0%, ${alpha(theme.palette.background.paper, 0.4)} 100%)`,
    backdropFilter: "blur(10px)",
    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    borderRadius: 8,
    transition: "all 0.3s ease",
    "&:hover": {
      border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
      background: `linear-gradient(135deg, ${alpha(
        theme.palette.background.paper,
        0.7
      )} 0%, ${alpha(theme.palette.background.paper, 0.5)} 100%)`,
    },
    "&.Mui-focused": {
      border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.1)}`,
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
  },
  "& .MuiInputLabel-root": {
    color: alpha(theme.palette.text.primary, 0.7),
    fontWeight: 500,
    "&.Mui-focused": {
      color: theme.palette.primary.main,
    },
  },
}));

// Scrollable area with custom scrollbar
export const GlassScrollArea = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  overflowY: "auto",
  width: "100%",
  padding: theme.spacing(1),
  position: "relative",
  "&::-webkit-scrollbar": {
    width: 6,
  },
  "&::-webkit-scrollbar-track": {
    background: alpha(theme.palette.background.default, 0.1),
    borderRadius: 3,
  },
  "&::-webkit-scrollbar-thumb": {
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.primary.main,
      0.3
    )}, ${alpha(theme.palette.primary.main, 0.2)})`,
    borderRadius: 3,
    "&:hover": {
      background: `linear-gradient(135deg, ${alpha(
        theme.palette.primary.main,
        0.4
      )}, ${alpha(theme.palette.primary.main, 0.3)})`,
    },
  },
}));

// Glassmorphic alert component
export const GlassAlert = styled(Alert)(({ theme }) => ({
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.error.main,
    0.1
  )} 0%, ${alpha(theme.palette.error.main, 0.05)} 100%)`,
  backdropFilter: "blur(10px)",
  border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
  borderRadius: 8,
  color: theme.palette.error.main,
  margin: theme.spacing(1),
  "& .MuiAlert-icon": {
    color: theme.palette.error.main,
  },
}));

// Empty state container
export const EmptyStateContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(6, 4),
  textAlign: "center",
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.background.paper,
    0.4
  )} 0%, ${alpha(theme.palette.background.paper, 0.2)} 100%)`,
  backdropFilter: "blur(8px)",
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  borderRadius: 12,
  margin: theme.spacing(2),
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    top: "50%",
    left: "50%",
    width: "100px",
    height: "100px",
    background: `radial-gradient(circle, ${alpha(
      theme.palette.primary.main,
      0.05
    )} 0%, transparent 70%)`,
    transform: "translate(-50%, -50%)",
    borderRadius: "50%",
  },
}));

// Loading container
export const LoadingContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  padding: theme.spacing(4),
  "& .MuiCircularProgress-root": {
    color: theme.palette.primary.main,
  },
}));

// Load more button
export const LoadMoreButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(2),
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.secondary.main,
    0.12
  )} 0%, ${alpha(theme.palette.secondary.main, 0.06)} 100%)`,
  backdropFilter: "blur(10px)",
  border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
  borderRadius: 8,
  color: theme.palette.secondary.main,
  fontWeight: 600,
  textTransform: "none",
  transition: "all 0.3s ease",
  "&:hover": {
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.secondary.main,
      0.18
    )} 0%, ${alpha(theme.palette.secondary.main, 0.09)} 100%)`,
    border: `1px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
    transform: "translateY(-1px)",
    boxShadow: `0 4px 16px ${alpha(theme.palette.secondary.main, 0.2)}`,
  },
}));
export default GlassTileLayout;
